import { Box, Typography, useMediaQuery, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import cs from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Permissions } from '../../../../shared/auth/constants/permissions';
import { useHasPermission } from '../../../Session';
import MobileFilterLink from '../../../components/MobileFilterLink';
import SearchInputField from '../../../components/fields/SearchInputField';
import useCommonStyles from '../../../useCommonStyles';

export type SaleTransactionsHeaderProps = {
    search: string;
    title?: string;
    searchOnChange: (searchValue: string) => void;
    setShowFilterDialog: (value: boolean) => void;
    setShowFilterMenu: (value: boolean) => void;
    downloadPdf?: () => void;
    downloadExcel?: () => void;
};

const SaleTransactionsHeader = ({
    search,
    title,
    searchOnChange,
    setShowFilterDialog,
    setShowFilterMenu,
    downloadExcel,
    downloadPdf,
}: SaleTransactionsHeaderProps) => {
    const { t } = useTranslation(['saleTransactionsPage', 'common']);
    const commonStyles = useCommonStyles();
    const theme = useTheme();
    const history = useHistory();

    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const canCreate = useHasPermission(Permissions.SALES_TRANSACTION_CREATE);
    const canExport = useHasPermission(Permissions.SALES_TRANSACTION_EXPORT);

    if (isSmall) {
        return (
            <Box>
                {title && (
                    <Box mb={3}>
                        <Typography className={commonStyles.title}>{title}</Typography>
                    </Box>
                )}
                <Box width="100%">
                    <SearchInputField onChange={searchOnChange} placeholder={t('common:search')} value={search} />
                </Box>
                <Box mt={3}>
                    <Box alignItems="center" display="flex">
                        <Box mr="auto">
                            <MobileFilterLink setShowFilterDialog={setShowFilterDialog} />
                        </Box>
                        <Box className={commonStyles.buttonGroup}>
                            {
                                // eslint-disable-next-line max-len
                                isSmall && canExport && (
                                    <Button color="secondary" onClick={downloadPdf} size="small" variant="contained">
                                        {
                                            // eslint-disable-next-line max-len
                                            <>
                                                <GetAppIcon
                                                    className={commonStyles.normalFontSize}
                                                    fontSize="small"
                                                    style={{ marginRight: 4 }}
                                                />
                                                {t('common:pdf')}
                                            </>
                                        }
                                    </Button>
                                )
                            }
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box alignItems="center" className={commonStyles.header} display="flex" justifyContent="flex-end">
            {title && (
                <Box>
                    <Typography className={commonStyles.title}>{title}</Typography>
                </Box>
            )}
            <Box ml="auto">
                <SearchInputField onChange={searchOnChange} placeholder={t('common:search')} value={search} />
            </Box>
            <Box className={cs(commonStyles.buttonGroup, commonStyles.buttonGroupHeader)}>
                <Button color="secondary" onClick={() => setShowFilterMenu(true)} size="large" variant="contained">
                    {t('saleTransactionsPage:filterBy')} <ExpandMoreIcon fontSize="small" />
                </Button>

                {canExport && (
                    <>
                        <Button color="secondary" onClick={downloadExcel} size="large" variant="contained">
                            <GetAppIcon fontSize="small" /> {t('common:exportInExcel')}
                        </Button>
                        <Button color="secondary" onClick={downloadPdf} size="large" variant="contained">
                            <GetAppIcon fontSize="small" /> {t('common:exportInPdf')}
                        </Button>
                    </>
                )}

                {canCreate && (
                    <Button
                        color="secondary"
                        onClick={() => history.push('/salesTransactions/create')}
                        size="large"
                        variant="contained"
                    >
                        <AddIcon fontSize="small" /> {t('common:create')}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default SaleTransactionsHeader;
